import { h, defineComponent } from 'vue';
import { TaskState } from '@/ui/concerns/useTask';
import { apiError } from '@/ui/dictionary';
import { StatusCodes } from 'http-status-codes';
import { message } from './Message';
import { useSetBrowserLocale } from '@/ui/concerns';
import { SUBSCRIPTION_INCOMPLETE } from '@/services/api';
import { namedLink } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';

export interface ApiErrorMessageProps {
  state: TaskState;
  customErrors: Record<number | string, string>;
  ignoreErrors: number[];
}

export const ApiErrorMessage = defineComponent<ApiErrorMessageProps>({
  name: 'ApiErrorMessage',
  props: {
    state: {
      type: Object,
      required: true,
    },
    customErrors: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    ignoreErrors: {
      type: Array,
      required: false,
      default: () => ([]),
    },
  },
  setup(props) {
    useSetBrowserLocale();

    return function render() {
      // if no error is set, don't display anything
      if (!('error' in props.state)) {
        return null;
      }

      // 422 is always ignored
      if (props.state.error.code === StatusCodes.UNPROCESSABLE_ENTITY) {
        return null;
      }

      // explicitly ignored errors are ignored
      if (props.ignoreErrors.includes(props.state.error.code)) {
        return null;
      }

      if (props.state.error.code === SUBSCRIPTION_INCOMPLETE) {
        return message('error', [
          h('div', [
            h('span', `${t('dictionary.api-error.SUBSCRIPTION_INCOMPLETE.first')} `),
            namedLink(['here'], 'subscription'),
            h('span', ` ${t('dictionary.api-error.SUBSCRIPTION_INCOMPLETE.second')}`),
          ]),
        ]);
      }

      // custom error can come as a number code or as an error message string
      const errorContent = props.state.error.code in props.customErrors
        ? props.customErrors[props.state.error.code]
        : props.state.error.message in props.customErrors
          ? props.customErrors[props.state.error.message]
          : apiError(props.state.error);

      // display error message based on error dictionary or provided custom error messages
      return message('error', errorContent);
    };
  },
});

export function apiErrorMessage(
  state: TaskState,
  customErrors: Record<number | string, string> = {},
  ignoreErrors: number[] = [],
) {
  return h(ApiErrorMessage, {
    props: {
      state,
      customErrors,
      ignoreErrors,
    },
  });
}
